import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { MqttMessage, Subscription } from '@/features/console/types';

interface MqttStore {
  subscriptions: Subscription[];
  messages: MqttMessage[];
  unreadCount: number;
  isConnected: boolean;

  subscribe: (sub: Omit<Subscription, 'messageCount'>) => void;
  unsubscribe: (id: string) => void;
  addMessage: (msg: MqttMessage) => void;
  markAllRead: () => void;
  clearMessages: () => void;
  setConnected: (v: boolean) => void;
}

export const useMqttStore = create<MqttStore>()(
  persist(
    (set, get) => ({
      subscriptions: [],
      messages: [],
      unreadCount: 0,
      isConnected: false,

      subscribe: (sub) => {
        if (get().subscriptions.some((s) => s.id === sub.id)) return;
        set((state) => ({
          subscriptions: [...state.subscriptions, { ...sub, messageCount: 0 }],
        }));
      },

      unsubscribe: (id) => {
        set((state) => ({
          subscriptions: state.subscriptions.filter((s) => s.id !== id),
        }));
      },

      addMessage: (msg) => {
        set((state) => {
          const entityId = msg.deveui ?? msg.gwid;
          const next = [...state.messages, msg];
          const trimmed = next.length > 500 ? next.slice(-500) : next;
          const subscriptions = state.subscriptions.map((sub) =>
            sub.id === entityId ? { ...sub, messageCount: sub.messageCount + 1 } : sub,
          );
          return { messages: trimmed, subscriptions, unreadCount: state.unreadCount + 1 };
        });
      },

      markAllRead: () => set({ unreadCount: 0 }),

      clearMessages: () =>
        set((state) => ({
          messages: [],
          unreadCount: 0,
          subscriptions: state.subscriptions.map((s) => ({ ...s, messageCount: 0 })),
        })),

      setConnected: (v) => set({ isConnected: v }),
    }),
    {
      name: 'ptns-mqtt',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        subscriptions: state.subscriptions,
        messages: state.messages,
        unreadCount: state.unreadCount,
        // isConnected excluded — always reconnect on reload
      }),
    },
  ),
);
